import React, { useEffect, useRef } from "react";
import { View, Text, Pressable, Modal, ActivityIndicator, StyleSheet, Image, ScrollView } from "react-native";
import { colors, textStyle } from "../styles";
import Avatar from "./Avatar";
import { useSharingStore } from "../store/sharingStore";
import { useSocketStore } from "../store/socketStore";
import { trackView, trackButton, View as MetricView, Button } from "../services/metrics";

const closeIcon = require("../assets/icons/close.png");
const shareIcon = require("../assets/icons/share.png");

/* Share modal design tokens (from the Figma "Share Modal" design). */
const SURFACE = "#070B18";        // surface/surface — card + unselected row bg
const ROW_SELECTED = "#262842";   // normal — selected row bg
const CLOSE_BG = "#5E5D90";       // close button circle
const CHECK_FILL = "#CED0FB";     // checked checkbox + Share button bg
const CHECK_OUTLINE = "#6E80CA";  // unchecked checkbox border
const ON_SURFACE = "#FBF8FE";     // title / row text
const FOREGROUND = "#130A45";     // icon/text on light fills

interface Props {
  visible: boolean;
  comicId: string;
  onClose: () => void;
}

export default function ShareModal({ visible, comicId, onClose }: Props) {
  const { shareTargets, sharesSent, isLoadingTargets, isSharing, shareError } = useSharingStore();
  const { getShareTargets, shareComic, listSharesSent } = useSocketStore();
  const [selectedIds, setSelectedIds] = React.useState<Set<string>>(new Set());
  // Recipients submitted in the current Share action; the modal closes once
  // they've all been confirmed (each arrives via the comicShared handler,
  // which appends to sharesSent).
  const submittedRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (visible) {
      trackView(MetricView.ShareModal);
      useSharingStore.getState().reset();
      useSharingStore.getState().setLoadingTargets(true);
      setSelectedIds(new Set());
      submittedRef.current = new Set();
      getShareTargets();
      listSharesSent(comicId);
    }
  }, [visible, comicId]);

  const alreadySentTo = new Set(sharesSent.map((s) => s.recipientId));

  const toggle = (customerId: string) => {
    if (alreadySentTo.has(customerId) || isSharing) return;
    trackButton(MetricView.ShareModal, Button.ShareModal.SelectTarget);
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(customerId)) next.delete(customerId);
      else next.add(customerId);
      return next;
    });
  };

  const handleShare = () => {
    const recipients = [...selectedIds].filter((id) => !alreadySentTo.has(id));
    if (recipients.length === 0) return;
    trackButton(MetricView.ShareModal, Button.ShareModal.Confirm);
    submittedRef.current = new Set(recipients);
    useSharingStore.getState().setSharing(true);
    useSharingStore.getState().setShareError(null);
    useSharingStore.getState().setShareSuccess(null);
    recipients.forEach((recipientId) => shareComic(comicId, recipientId));
  };

  const handleClose = () => {
    useSharingStore.getState().reset();
    onClose();
  };

  const handleClosePress = () => {
    trackButton(MetricView.ShareModal, Button.ShareModal.Close);
    handleClose();
  };

  // Resolve the current Share batch. Close once every submitted recipient is
  // confirmed; on any error stop the spinner and surface it (the server's
  // shareError isn't recipient-specific, so we can't keep waiting on the rest —
  // recipients that did succeed already show as sent), letting the user dismiss.
  useEffect(() => {
    if (!visible) return;
    const submitted = submittedRef.current;
    if (submitted.size === 0) return;
    const allDone = [...submitted].every((id) => alreadySentTo.has(id));
    if (allDone) {
      handleClose();
    } else if (shareError) {
      useSharingStore.getState().setSharing(false);
      submittedRef.current = new Set(); // stop watching this batch
    }
  }, [sharesSent, shareError, visible]);

  const canShare = [...selectedIds].some((id) => !alreadySentTo.has(id)) && !isSharing;

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={handleClosePress}>
      <View style={styles.centerer}>
        <Pressable style={styles.backdrop} onPress={handleClosePress} />
        <View style={styles.modal}>
          {/* Header */}
          <View style={styles.header}>
            <Text style={styles.title} numberOfLines={1}>Share</Text>
            <Pressable style={styles.closeBtn} onPress={handleClosePress} accessibilityLabel="Close">
              <Image source={closeIcon} style={styles.closeIcon} resizeMode="contain" />
            </Pressable>
          </View>

          {/* Body */}
          {isLoadingTargets ? (
            <View style={styles.loadingWrap}>
              <ActivityIndicator size="small" color={colors.white} />
              <Text style={styles.loadingText}>Finding siblings...</Text>
            </View>
          ) : shareTargets.length === 0 ? (
            <Text style={shareError ? styles.errorText : styles.loadingText}>
              {shareError || "No siblings found in your household."}
            </Text>
          ) : (
            <ScrollView style={styles.listScroll} contentContainerStyle={styles.list}>
              {shareTargets.map((target) => {
                const sent = alreadySentTo.has(target.customerId);
                const checked = sent || selectedIds.has(target.customerId);
                return (
                  <Pressable
                    key={target.customerId}
                    style={[styles.row, checked && styles.rowSelected, sent && styles.rowSent]}
                    onPress={() => toggle(target.customerId)}
                    disabled={sent || isSharing}
                  >
                    <Avatar size={48} name={target.name} uri={target.avatarUri} />
                    <Text style={styles.rowName} numberOfLines={1}>
                      {target.name || "Sibling"}
                    </Text>
                    <View style={[styles.checkbox, checked ? styles.checkboxChecked : styles.checkboxUnchecked]}>
                      {checked && <View style={styles.checkMark} />}
                    </View>
                  </Pressable>
                );
              })}
            </ScrollView>
          )}

          {shareError && shareTargets.length > 0 && (
            <Text style={styles.errorText}>{shareError}</Text>
          )}

          {/* Share button */}
          <Pressable
            style={[styles.shareBtn, !canShare && styles.shareBtnDisabled]}
            onPress={handleShare}
            disabled={!canShare}
          >
            {isSharing ? (
              <ActivityIndicator size="small" color={FOREGROUND} />
            ) : (
              <Image source={shareIcon} style={styles.shareBtnIcon} resizeMode="contain" />
            )}
            <Text style={styles.shareBtnText}>Share</Text>
          </Pressable>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  backdrop: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0,0,0,0.6)",
  },
  centerer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  modal: {
    backgroundColor: SURFACE,
    borderRadius: 64,
    paddingHorizontal: 16,
    paddingTop: 24,
    paddingBottom: 32,
    gap: 24,
    width: 480,
    maxWidth: "90%",
    maxHeight: "80%",
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    height: 48,
    paddingHorizontal: 24,
    gap: 8,
  },
  title: {
    ...textStyle.body,
    flex: 1,
    fontSize: 28,
    lineHeight: 28 * 1.2,
    color: ON_SURFACE,
  },
  closeBtn: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: CLOSE_BG,
    alignItems: "center",
    justifyContent: "center",
  },
  closeIcon: {
    width: 16,
    height: 16,
    tintColor: colors.white,
  },
  loadingWrap: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 12,
    paddingVertical: 24,
  },
  loadingText: {
    ...textStyle.body,
    color: colors.white,
    fontSize: 14,
    textAlign: "center",
  },
  listScroll: {
    width: "100%",
  },
  list: {
    padding: 8,
  },
  row: {
    flexDirection: "row",
    alignItems: "center",
    minHeight: 56,
    paddingVertical: 14,
    paddingLeft: 16,
    paddingRight: 16,
    borderRadius: 9999,
    backgroundColor: SURFACE,
  },
  rowSelected: {
    backgroundColor: ROW_SELECTED,
  },
  rowSent: {
    opacity: 0.5,
  },
  rowName: {
    ...textStyle.body,
    flex: 1,
    fontSize: 16,
    lineHeight: 16 * 1.5,
    color: colors.white,
    paddingHorizontal: 16,
  },
  checkbox: {
    width: 24,
    height: 24,
    borderRadius: 8,
    alignItems: "center",
    justifyContent: "center",
  },
  checkboxUnchecked: {
    borderWidth: 2,
    borderColor: CHECK_OUTLINE,
  },
  checkboxChecked: {
    backgroundColor: CHECK_FILL,
  },
  // Checkmark drawn as an L-shape (two borders) rotated 45° — no asset needed.
  checkMark: {
    width: 6,
    height: 11,
    marginTop: -2,
    borderRightWidth: 2,
    borderBottomWidth: 2,
    borderColor: FOREGROUND,
    transform: [{ rotate: "45deg" }],
  },
  errorText: {
    ...textStyle.body,
    fontSize: 14,
    color: colors.danger,
    textAlign: "center",
  },
  shareBtn: {
    alignSelf: "center",
    backgroundColor: CHECK_FILL,
    borderRadius: 9999,
    height: 40,
    width: 176,
    paddingHorizontal: 20,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
  },
  shareBtnDisabled: {
    opacity: 0.4,
  },
  shareBtnIcon: {
    width: 18,
    height: 18,
    tintColor: FOREGROUND,
  },
  shareBtnText: {
    ...textStyle.body,
    fontSize: 16,
    lineHeight: 16 * 1.5,
    color: FOREGROUND,
  },
});
